'use client'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import { cn } from '@/lib/utils'
import { ChevronDown, CircleX, LoaderCircle } from 'lucide-react'
import { memo, useCallback, useRef, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import type { ReasoningMessagePartComponent } from '@assistant-ui/react'
import { MarkdownText } from './markdown-text'
import { IconV2ThoughtOutline } from '@icons/IconV2ThoughtOutline'

export type ReasoningVariant = 'outline' | 'ghost' | 'muted'

export type ReasoningRootProps = Omit<ComponentPropsWithoutRef<typeof Collapsible>, 'open' | 'onOpenChange'> & {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultOpen?: boolean
  streaming?: boolean
  incomplete?: boolean
  variant?: ReasoningVariant
}

export function ReasoningRoot({
  children,
  className,
  defaultOpen = false,
  onOpenChange,
  open: controlledOpen,
  streaming = false,
  incomplete = false,
  variant = 'muted',
  ...props
}: ReasoningRootProps) {
  const initialOpenRef = useRef(defaultOpen)
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : (userOpen ?? (streaming || initialOpenRef.current))

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setUserOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [isControlled, onOpenChange]
  )

  return (
    <Collapsible
      {...props}
      className={cn(
        'mb-3 w-full',
        {
          'rounded-lg border border-border/50 px-3 py-2': variant === 'outline',
          'rounded-lg bg-muted/50 px-3 py-2': variant === 'muted',
        },
        className
      )}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      {children}
      <span aria-atomic="true" aria-live="polite" className="sr-only" role="status">
        {streaming
          ? 'Reasoning summary is streaming.'
          : incomplete
            ? 'Reasoning summary did not complete.'
            : 'Reasoning summary is complete.'}
      </span>
    </Collapsible>
  )
}

export type ReasoningTriggerProps = ComponentPropsWithoutRef<typeof CollapsibleTrigger> & {
  active?: boolean
  duration?: number
  incomplete?: boolean
  children?: ReactNode
}

export function ReasoningTrigger({
  active = false,
  children = 'Reasoning summary',
  className,
  duration,
  incomplete = false,
  ...props
}: ReasoningTriggerProps) {
  const durationText = duration ? ` (${duration}s)` : ''

  return (
    <CollapsibleTrigger
      {...props}
      className={cn(
        'group flex w-full items-center justify-between gap-3 text-left text-sm text-foreground/50 transition-colors hover:text-foreground',
        className
      )}
      type="button"
    >
      <span className="flex min-w-0 items-center gap-2">
        {active ? (
          <LoaderCircle aria-hidden="true" className="size-4 shrink-0 animate-spin text-muted-foreground" />
        ) : incomplete ? (
          <CircleX aria-hidden="true" className="size-4 shrink-0 text-error" />
        ) : (
          <IconV2ThoughtOutline aria-hidden="true" className="size-4 shrink-0" />
        )}
        <span className="truncate">
          {children}
          {durationText}
        </span>
      </span>
      <ChevronDown
        aria-hidden="true"
        className="size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
      />
    </CollapsibleTrigger>
  )
}

export type ReasoningContentProps = ComponentPropsWithoutRef<typeof CollapsibleContent>

export function ReasoningContent({ children, className, ...props }: ReasoningContentProps) {
  return (
    <CollapsibleContent
      {...props}
      className={cn(
        'overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
        className
      )}
    >
      {children}
    </CollapsibleContent>
  )
}

export type ReasoningTextProps = ComponentPropsWithoutRef<'div'>

export function ReasoningText({ children, className, ...props }: ReasoningTextProps) {
  return (
    // <div {...props} className={cn('max-h-64 min-w-0 overflow-y-auto px-1 pt-3 text-foreground/50', className)}>
    <div {...props} className={cn('min-w-0 overflow-y-auto px-1 pt-3 text-foreground/50', className)}>
      {children}
    </div>
  )
}

export const Reasoning: ReasoningMessagePartComponent = memo(() => <MarkdownText />)
